import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Navbar } from "../../components/layout/Navbar";
import { Footer } from "../../components/layout/Footer";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Slider } from "../../components/ui/slider";
import { Package, Search, Filter, X, Grid, List, Heart, Plus, Minus, ShoppingCart } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useCart } from "../../context/CartContext";
import { useLanguage } from "../../context/LanguageContext";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Medical storage hero image
const HERO_IMAGE = "https://images.unsplash.com/photo-1671108503276-1d3d5ab23a3a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwyfHxwaGFybWFjeSUyMG1lZGljaW5lJTIwc2hlbHZlcyUyMHdhcmVob3VzZSUyMGludmVudG9yeXxlbnwwfHx8fDE3NzI4NDAyOTR8MA&ixlib=rb-4.1.0&q=85";

const ProductsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, getAuthHeaders } = useAuth();
  const { addItem } = useCart();
  const { t, isRTL } = useLanguage();
  
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [favorites, setFavorites] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState("grid");
  const [quantities, setQuantities] = useState({});
  
  // Filters
  const [search, setSearch] = useState(searchParams.get("search") || "");
  const [categoryId, setCategoryId] = useState(searchParams.get("category") || "");
  const [priceRange, setPriceRange] = useState([0, 500]);
  const [country, setCountry] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [prodRes, catRes] = await Promise.all([
          axios.get(`${API}/products`, {
            params: {
              search: search || undefined,
              category_id: categoryId || undefined,
              min_price: priceRange[0] || undefined,
              max_price: priceRange[1] < 500 ? priceRange[1] : undefined,
              country: country || undefined,
            },
          }),
          axios.get(`${API}/categories`),
        ]);
        setProducts(prodRes.data);
        setCategories(catRes.data);
        
        // Fetch favorites if logged in
        if (user) {
          try {
            const favRes = await axios.get(`${API}/favorites`, {
              headers: getAuthHeaders(),
            });
            setFavorites(favRes.data.map((p) => p.id));
          } catch (e) {
            console.error("Failed to fetch favorites");
          }
        }
      } catch (error) {
        console.error("Failed to fetch products:", error);
        toast.error("Failed to load products");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [search, categoryId, priceRange, country, user, getAuthHeaders]);

  const handleSearch = (e) => {
    e.preventDefault();
    setSearchParams({ search, category: categoryId });
  };

  const toggleFavorite = async (productId) => {
    if (!user) {
      toast.error("Please login to add favorites");
      return;
    }
    
    try {
      if (favorites.includes(productId)) {
        await axios.delete(`${API}/favorites/${productId}`, {
          headers: getAuthHeaders(),
        });
        setFavorites((prev) => prev.filter((id) => id !== productId));
        toast.success("Removed from favorites");
      } else {
        await axios.post(`${API}/favorites/${productId}`, {}, {
          headers: getAuthHeaders(),
        });
        setFavorites((prev) => [...prev, productId]);
        toast.success("Added to favorites");
      }
    } catch (error) {
      toast.error("Failed to update favorites");
    }
  };

  const handleAddToCart = (product) => {
    const qty = quantities[product.id] || 1;
    addItem(product, qty);
    toast.success(`${qty} x ${product.name} ${t("addedToCart")}`);
    // Reset quantity after adding
    setQuantities(prev => ({ ...prev, [product.id]: 1 }));
  };

  const updateProductQuantity = (productId, delta) => {
    setQuantities(prev => {
      const current = prev[productId] || 1;
      const newQty = Math.max(1, current + delta);
      return { ...prev, [productId]: newQty };
    });
  };

  const setProductQuantity = (productId, value) => {
    const qty = Math.max(1, parseInt(value) || 1);
    setQuantities(prev => ({ ...prev, [productId]: qty }));
  };

  const clearFilters = () => {
    setSearch("");
    setCategoryId("");
    setPriceRange([0, 500]);
    setCountry("");
    setSearchParams({});
  };

  return (
    <div className="min-h-screen bg-background" dir={isRTL ? "rtl" : "ltr"}>
      <Navbar />
      
      {/* Hero Header with Medical Storage Image */}
      <section className="relative py-20 bg-[#0f172a]" data-testid="products-header">
        <div className="absolute inset-0">
          <img src={HERO_IMAGE} alt="Medical Storage" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0f172a] via-[#0f172a]/80 to-transparent" />
        </div>
        <div className="container mx-auto px-4 md:px-8 max-w-7xl relative z-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <Badge className="mb-4 bg-[#d4af37] text-[#0f172a]">{t("products")}</Badge>
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-white">
                {t("browseProducts")}
              </h1>
              <p className="text-slate-300 mt-2">
                {products.length} {t("productsAvailable")}
              </p>
            </div>
            
            {/* Search Bar */}
            <form onSubmit={handleSearch} className="flex gap-2 w-full md:w-auto">
              <div className="relative flex-1 md:w-80">
                <Search className={`absolute ${isRTL ? 'right-3' : 'left-3'} top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground`} />
                <Input
                  type="text"
                  placeholder={t("searchProducts")}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className={`${isRTL ? 'pr-10' : 'pl-10'} h-12 bg-white`}
                  data-testid="search-input"
                />
              </div>
              <Button type="submit" className="bg-[#1a365d] hover:bg-[#1a365d]/90 h-12">
                Search
              </Button>
            </form>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <div className="container mx-auto px-4 md:px-8 max-w-7xl py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Filters Sidebar */}
          <aside className={`lg:w-64 flex-shrink-0 ${showFilters ? "block" : "hidden lg:block"}`}>
            <Card className="sticky top-24">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="font-heading font-semibold">{t("filters")}</h3>
                  <Button variant="ghost" size="sm" onClick={clearFilters}>
                    {t("clearAll")}
                  </Button>
                </div>
                
                {/* Category Filter */}
                <div className="mb-6">
                  <Label className="mb-2 block">{t("category")}</Label>
                  <Select value={categoryId || "all"} onValueChange={(v) => setCategoryId(v === "all" ? "" : v)}>
                    <SelectTrigger data-testid="category-filter">
                      <SelectValue placeholder={t("allCategories")} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t("allCategories")}</SelectItem>
                      {categories.map((cat) => (
                        <SelectItem key={cat.id} value={cat.id}>
                          {isRTL ? cat.name_ar || cat.name : cat.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Price Range Filter */}
                <div className="mb-6">
                  <Label className="mb-4 block">
                    {t("priceRange")}: {priceRange[0]} - {priceRange[1]} {t("lyd")}
                  </Label>
                  <Slider
                    value={priceRange}
                    onValueChange={setPriceRange}
                    min={0}
                    max={500}
                    step={10}
                    className="mt-2"
                    data-testid="price-filter"
                  />
                </div>
                
                {/* Country Filter */}
                <div className="mb-6">
                  <Label className="mb-2 block">{t("countryOfOrigin")}</Label>
                  <Select value={country || "all"} onValueChange={(v) => setCountry(v === "all" ? "" : v)}>
                    <SelectTrigger data-testid="country-filter">
                      <SelectValue placeholder={t("allCountries")} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t("allCountries")}</SelectItem>
                      <SelectItem value="USA">{t("usa")}</SelectItem>
                      <SelectItem value="UK">{t("uk")}</SelectItem>
                      <SelectItem value="Germany">{t("germany")}</SelectItem>
                      <SelectItem value="France">{t("france")}</SelectItem>
                      <SelectItem value="India">{t("india")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>
          </aside>

          {/* Products Grid */}
          <div className="flex-1">
            {/* Toolbar */}
            <div className="flex items-center justify-between mb-6">
              <Button
                variant="outline"
                className="lg:hidden"
                onClick={() => setShowFilters(!showFilters)}
                data-testid="toggle-filters"
              >
                <Filter className="h-4 w-4 mr-2" />
                {t("filters")}
              </Button>
              
              <div className="flex items-center gap-2">
                <Button
                  variant={viewMode === "grid" ? "default" : "ghost"}
                  size="icon"
                  onClick={() => setViewMode("grid")}
                  data-testid="grid-view"
                >
                  <Grid className="h-5 w-5" />
                </Button>
                <Button
                  variant={viewMode === "list" ? "default" : "ghost"}
                  size="icon"
                  onClick={() => setViewMode("list")}
                  data-testid="list-view"
                >
                  <List className="h-5 w-5" />
                </Button>
              </div>
            </div>

            {/* Products */}
            {loading ? (
              <div className={`grid ${viewMode === "grid" ? "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3" : "grid-cols-1"} gap-6`}>
                {Array(6).fill(0).map((_, i) => (
                  <div key={i} className="h-80 rounded-xl bg-muted animate-pulse" />
                ))}
              </div>
            ) : products.length === 0 ? (
              <Card className="p-12 text-center">
                <Package className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                <h3 className="font-heading font-semibold text-lg mb-2">{t("noProductsFound")}</h3>
                <p className="text-muted-foreground mb-4">{t("tryAdjustingFilters")}</p>
                <Button onClick={clearFilters} variant="outline">
                  {t("clearFilters")}
                </Button>
              </Card>
            ) : (
              <div className={`grid ${viewMode === "grid" ? "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3" : "grid-cols-1"} gap-6`}>
                {products.map((product, index) => (
                  <motion.div
                    key={product.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <Card className={`group overflow-hidden hover:shadow-xl transition-all duration-300 ${viewMode === "list" ? "flex" : ""}`} data-testid={`product-card-${product.id}`}>
                      <div className={`${viewMode === "list" ? "w-48 flex-shrink-0" : "aspect-square"} bg-gradient-to-br from-slate-100 to-slate-50 relative overflow-hidden`}>
                        {product.images?.[0] ? (
                          <img
                            src={product.images[0]}
                            alt={isRTL ? product.name_ar || product.name : product.name}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Package className="w-16 h-16 text-slate-300" />
                          </div>
                        )}
                        
                        {/* Badges */}
                        <div className={`absolute top-3 ${isRTL ? 'right-3' : 'left-3'} flex flex-col gap-2`}>
                          {product.discount > 0 && (
                            <Badge className="bg-red-500 text-white">-{product.discount}%</Badge>
                          )}
                          {product.stock_quantity <= product.min_stock_alert && (
                            <Badge variant="outline" className="bg-white text-orange-600 border-orange-600">
                              {t("lowStock")}
                            </Badge>
                          )}
                        </div>
                        
                        {/* Favorite Button */}
                        <button
                          onClick={() => toggleFavorite(product.id)}
                          className={`absolute top-3 ${isRTL ? 'left-3' : 'right-3'} p-2 bg-white rounded-full shadow-md hover:scale-110 transition-transform`}
                          data-testid={`favorite-${product.id}`}
                        >
                          <Heart
                            className={`w-5 h-5 ${favorites.includes(product.id) ? "fill-red-500 text-red-500" : "text-slate-400"}`}
                          />
                        </button>
                      </div>
                      
                      <CardContent className={`p-6 ${viewMode === "list" ? "flex-1 flex flex-col justify-between" : ""}`}>
                        <div>
                          <p className="text-xs text-[#d4af37] font-medium uppercase tracking-wide mb-1">
                            {product.brand || "Bahr Al Rimal"}
                          </p>
                          <Link to={`/products/${product.id}`}>
                            <h3 className="font-heading font-semibold text-lg mb-2 group-hover:text-[#1a365d] transition-colors line-clamp-2">
                              {isRTL ? product.name_ar || product.name : product.name}
                            </h3>
                          </Link>
                          {viewMode === "list" && product.description && (
                            <p className="text-muted-foreground text-sm mb-4 line-clamp-2">
                              {product.description}
                            </p>
                          )}
                        </div>
                        
                        <div className="flex items-center justify-between mt-auto">
                          <div>
                            {product.discount > 0 ? (
                              <>
                                <span className="text-xl font-bold text-[#1a365d]">
                                  {(product.sale_price * (1 - product.discount / 100)).toFixed(2)} {t("lyd")}
                                </span>
                                <span className={`text-sm text-muted-foreground line-through ${isRTL ? 'mr-2' : 'ml-2'}`}>
                                  {product.sale_price} {t("lyd")}
                                </span>
                              </>
                            ) : (
                              <span className="text-xl font-bold text-[#1a365d]">
                                {product.sale_price} {t("lyd")}
                              </span>
                            )}
                          </div>
                        </div>
                        
                        {/* Quantity and Add to Cart */}
                        <div className="flex items-center gap-2 mt-4 pt-4 border-t">
                          <div className="flex items-center border rounded-md">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => updateProductQuantity(product.id, -1)}
                              data-testid={`qty-minus-${product.id}`}
                            >
                              <Minus className="h-4 w-4" />
                            </Button>
                            <input
                              type="number"
                              min="1"
                              value={quantities[product.id] || 1}
                              onChange={(e) => setProductQuantity(product.id, e.target.value)}
                              className="w-12 h-8 text-center border-0 focus:outline-none"
                              data-testid={`qty-input-${product.id}`}
                            />
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => updateProductQuantity(product.id, 1)}
                              data-testid={`qty-plus-${product.id}`}
                            >
                              <Plus className="h-4 w-4" />
                            </Button>
                          </div>
                          <Button
                            className="flex-1 bg-[#1a365d] hover:bg-[#1a365d]/90"
                            onClick={() => handleAddToCart(product)}
                            data-testid={`add-to-cart-${product.id}`}
                          >
                            <ShoppingCart className="h-4 w-4 mr-1" />
                            {t("add")}
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ProductsPage;
